<script>
import {defineComponent, computed} from "vue";
import {useRoute, useRouter} from "vue-router";
import LoadingSpinner from "@/javascript/views/components/loadingSpinner.vue";
import PageHeader from "@/javascript/views/components/landing/PageHeader.vue";
import HeroBanner from "@/javascript/views/components/landing/HeroBanner.vue";
import PageFooter from "@/javascript/views/components/landing/PageFooter.vue";
import InfoSection from "@/javascript/views/components/landing/InfoSection.vue";
import DonatableTargetsList from "../components/landing/DonatableTargetsList.vue";
import GiftBasketSideBarButton from "./GiftBasketSidebarButton.vue";
import {useLandingPageStore} from "../../stores/landingPage";
import {useAnalyticEventStore} from "@/javascript/stores/analyticEvents";
import {ApplicationHelper} from "../../helpers/ApplicationHelper";
import {useUserStore} from "@/javascript/stores/user";
import DashboardHeader from "@/javascript/views/components/dashboard/DashboardHeader.vue";
import Icon from "@/javascript/views/components/Icon.vue";
import PageArticle from "../components/landing/pageArticle.vue";
import LandingPage from "./LandingPage.vue";
import GenericError from "../components/generic-error.vue";



export default defineComponent({
  /**
   * This component is the wrapper for the entire client website
   * It will hold multiple pages, navigation, etc for the site
   * Currently is only rending the Landing Page (only allowed type)
   */
  name: "ClientWebsite",
  components: {
    GenericError,
    LandingPage,
    PageArticle,
    GiftBasketSideBarButton,
    InfoSection,
    LoadingSpinner,
    PageHeader,
    HeroBanner,
    PageFooter,
    DonatableTargetsList,
    DashboardHeader,
    Icon,
  },
  props: {},
  setup() {
    const route = useRoute();
    const router = useRouter();
    const landingPageStore = useLandingPageStore();
    const analyticEventStore = useAnalyticEventStore();
    const userStore = useUserStore();
    const isLandingPage = computed(() => {
      return route.name === 'landing';
    });

    return {
      landingPageStore,
      analyticEventStore,
      route,
      router,
      userStore,
      isLandingPage
    };
  },
  data() {
    return {
      customization: null,
      showDonationModal: false,
      loading: false,
      hasNavigatedToLanding: false,
      isScrolled: false,
    };
  },
  computed: {
    organizationId() {
      return this.landingPageStore?.organizationId || this.route.params.org;
    },
    campaigns() {
      return this.landingPageStore.campaigns;
    },
    pageData() {
      // Only landing page is set up right now
      // This needs to determine page type once multiple pages/ navigation is implemented
      return this.landingPageStore.publishedLayout?.pages?.find((page) => page.page_type === 'landing_page');
    },
    organization() {
      return this.landingPageStore.organization;
    },
    brand() {
      return this.landingPageStore.localBrand;
    },
    isLoggedIn() {
      // If we’ve already loaded a user, use that user otherwise fall back
      return !!(this.userStore.user || document.body.dataset.currentUserId);
    },
  },
  watch: {
    '$refs.pageContainer'() {
      if (this.$refs.pageContainer) {
        this.$refs.pageContainer.addEventListener('scroll', this.handleScroll);
      }
    },
    loading(val) {
      if (!val) {
        //nextTick waits for Vue to finishing updating after loading
        this.$nextTick(() => {
          if (this.$refs.pageContainer) {
            this.$refs.pageContainer.addEventListener('scroll', this.handleScroll);
          }
        });
      }
    }
  },
  mounted() {
    this.loading = true;

    const currentUserId = document.body.dataset.currentUserId;
    if (currentUserId && !this.userStore.userId) {
      this.userStore.userId = currentUserId;
      this.userStore.getUser();
    }
    if(this.route.params.org){
      this.landingPageStore.setOrganizationId(this.route.params.org);
    }

    this.landingPageStore.loadPublishedPage().then(() => {
      this.analyticEventStore.setOrganizationId(this.landingPageStore.organizationId);
      if (this.route.query.source !== 'qr') {
        this.analyticEventStore.logEvent({ action: 'landing-page-visit' });
      }
      if (!this.organization && !this.landingPageStore.publishedLayout) {
        this.router.push({path: '/error'});
      } else if (this.organization) {
        document.title = this.organization.name || 'Donate';
      }
    }).catch((e) => {
      console.error('error', e);
      this.router.push({path: '/error'});
    }).finally(_ => {
      this.loading = false;
    });
  },
  beforeUnmount() {
    if (this.$refs.pageContainer) this.$refs.pageContainer.removeEventListener('scroll', this.handleScroll);
  },

  methods: {
    componentByType(type) {
      return this.pageData?.components?.find(component => component.component_type === type);
    },

    componentValues(type) {
      const component = this.componentByType(type);
      return this.landingPageStore.getComponentValues(component);
    },
    donateButtonClicked() {
      this.showDonationModal = true;
      this.analyticEventStore.logEvent({
        action: `landing-donate-button-clicked`
      });
    },
    logoClicked() {
      console.log('logo clicked data', this.pageData);
      const logoUrl = this.componentByType('header')?.component_data?.logoUrl.value;
      console.log('url', logoUrl);

      if (this.route.name !== 'landing') {
        this.router.push({name: 'landing', params: this.route.params});
        this.hasNavigatedToLanding = true;
      } else if (logoUrl) {
        ApplicationHelper.redirectToExternalUrl(logoUrl);
      }
    },
    goToGivingForm() {
      this.router.push({ name: 'donation-form', params: { org: this.route.params.org } });
    },

    handleScroll() {
      const container = this.$refs.pageContainer;
      const scrollTop = container ? container.scrollTop : window.scrollY;
      this.isScrolled = scrollTop > 100; // 100px is roughly past the header
    }
  }
});

</script>

<template>
  <div
    v-if="loading"
    class="loading-spinner-container"
  >
    <LoadingSpinner />
  </div>
  <div v-else-if="!organization">
    <generic-error />
  </div>
  <div v-else-if="!landingPageStore.publishedLayout">
    <GenericError
      error-title="You&apos;re almost there!"
      error-message="The organization exists, but their donation page hasn't been published yet."
      page-title="Page Not Published"
    />
  </div>
  <div
    v-else
    ref="pageContainer"
    class="page-container"
  >
    <page-header
      :org-name="organization?.name"
      :logo-resource="brand?.logo"
      :show-logo="componentValues('header')?.logo"
      :donate-button-background-color="brand.color_primary"
      :donate-button-hover-background-color="brand.color_primary_dark"
      fallback="name"
      :ignore-org="false"
      @logo-click="logoClicked"
    >
      <template #right-icon-slot>
        <div class="flex items-center gap-3">
          <!-- Gift Basket Button -->
          <gift-basket-side-bar-button />

          <!-- Hamburger Menu -->
          <!--          <dashboard-header title="" class="relative compact-hamburger">-->
          <!--            <template #nav-menu-items>-->
          <!--              <nav class="nav-menu">-->
          <!--                &lt;!&ndash; Logged in: go to donor dashboard &ndash;&gt;-->
          <!--                <div-->
          <!--                  v-if="isLoggedIn"-->
          <!--                  class="nav-item flex flex-row justify-between"-->
          <!--                  @click="router.push({ name: 'donor-dashboard', params: { org: organizationId, panel: 'profile' } })"-->
          <!--                >-->
          <!--                  <div>Dashboard</div>-->
          <!--                </div>-->

          <!--                &lt;!&ndash; Logged out: go straight to Devise login &ndash;&gt;-->
          <!--                <a-->
          <!--                  v-else-->
          <!--                  class="nav-item flex flex-row justify-between"-->
          <!--                  href="/users/sign_in"-->
          <!--                >-->
          <!--                  <div>Log In</div>-->
          <!--                </a>-->

          <!--                &lt;!&ndash; Sign Out &ndash;&gt;-->
          <!--                <div-->
          <!--                  v-if="isLoggedIn"-->
          <!--                  class="nav-item flex flex-row justify-between"-->
          <!--                  @click="userStore.signOut"-->
          <!--                >-->
          <!--                  <div>Sign Out</div>-->
          <!--                  <icon icon="logout" color="#4f4f4f"/>-->
          <!--                </div>-->
          <!--              </nav>-->
          <!--            </template>-->
          <!--          </dashboard-header>-->
        </div>
      </template>
    </page-header>
    <!-- Replace this when we implement multiple pages -->
    <landing-page />
    <page-footer
      v-bind="componentValues('footer')"
      :layout="componentValues('footer')?.showFooter ? 'contact' : 'basic'"
      :logo-src="componentValues('footer')?.logo ? organization.logo_url : null"
      :organization="organization"
      :background-color="landingPageStore.getFooterBackgroundColor()"
      :text-color="landingPageStore.getContrastTextColor(landingPageStore.getFooterBackgroundColor())"
      disable-navigation
    />

    <!-- Floating Give Now Button -->
    <button
      v-if="isLandingPage && landingPageStore.hasPaymentProcessor"
      class="floating-give-btn"
      :style="{
        backgroundColor: landingPageStore.localBrand?.color_primary,
        color: landingPageStore.getContrastTextColor(landingPageStore.localBrand?.color_primary)
      }"
      @click="goToGivingForm"
    >
      <icon
        icon="hand-heart"
        size="28px"
        :color="landingPageStore.getContrastTextColor(landingPageStore.localBrand?.color_primary)"
      />
    </button>
  </div>
</template>

<style lang="scss" scoped>
@import '@/styles/_variables.scss';

.loading-spinner-container {
  width: 100vw;
  height: 100vh;
  display: flex;
  justify-content: center;
  align-items: center;

}

.page-container {
  overflow-y: auto !important;
  height: 100vh;

  // setup container widths
  .outer-container {
    background-color: $color-section-bg;
    width: 100%;
    display: flex;
    justify-content: center;

    :deep(.inner-container) {
      width: 100%;
      max-width: $content-max-width;
      padding: $space-xl $space-xl;
      margin: $space-lg;
    }
  }

  :deep(.header) {
    background: transparent !important;
    box-shadow: none !important;
    height: 64px !important; /* match default header height */
    display: flex;
    align-items: center;
  }

  :deep(.header-container) {
    height: 64px !important; /* match header height */
    display: flex;
    align-items: center;
    padding-right: 0;
  }

  :deep(.hamburger-btn) {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 40px;
    width: 40px;
  }

  :deep(.menu-section) {
    margin-right: 0 !important;
  }

  :deep(.menu-section .dropdown-menu) {
    z-index: 1000;
  }

  /* Kill the big left block reserved */
  .compact-hamburger :deep(.logo-container),
  .compact-hamburger :deep(.title-medium) {
    display: none !important;
    width: 0 !important;
    padding: 0 !important;
  }

  // tablet screens
  @media screen and (min-width: $breakpoint-min-sm) and (max-width: $breakpoint-max-lg) {
    .outer-container {
      :deep(.inner-container) {
        padding: $space-3xl;
      }
    }
  }
  // mobile screens
  @media screen and (max-width: $breakpoint-max-sm) {
    .outer-container {
      :deep(.inner-container) {
        padding: $space-xl $space-xl;
      }
    }
  }

  .give-now-btn {
    @apply flex items-center justify-center text-sm font-semibold rounded-lg cursor-pointer;
    padding: 8px 16px;
    border: none !important;
    outline: none;
    gap: 8px;
    white-space: nowrap;
    transition: all 0.3s ease;

    @screen md {
      @apply text-lg;
      padding: 8px 20px;
    }

    &:hover {
      filter: brightness(0.9);
    }

    &.give-now-sticky {
      position: fixed;
      top: 16px;
      // Positions button just outside the right edge of the 1200px content container. We want the button right outside the components
      // (100vw - 1200px) / 2 = right margin of container, - 72px = offset for button width + spacing.
      // Update 1200px if the container max-width changes.
      right: calc((100vw - 1200px) / 2 - 72px);
      z-index: 40;
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
      border-radius: 50%;
      width: 56px;
      height: 56px;
      padding: 0;
      transition: all 0.3s ease;

      @media screen and (max-width: 1400px) {
        right: 16px;
      }

      @media screen and (max-width: 768px) {
        opacity: 0.8;

        &:hover, &:active {
          opacity: 1;
        }
      }
    }
  }
}

.floating-give-btn {
  position: fixed;
  bottom: 24px;
  // Positions button just outside the right edge of the 1200px content container.
  // (100vw - 1200px) / 2 = right margin of container, - 72px = offset for button width + spacing.
  // Update 1200px if $content-max-width changes.
  right: calc((100vw - 1200px) / 2 - 72px);
  z-index: 40;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 60px;
  height: 60px;
  border-radius: 50%;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  cursor: pointer;
  transition: all 0.2s ease;
  border: none;
  padding: 0;

  &:hover {
    filter: brightness(0.9);
    box-shadow: 0 6px 16px rgba(0, 0, 0, 0.2);
  }

  @media screen and (max-width: 1400px) {
    right: 16px;
  }

  @media screen and (max-width: 768px) {
    bottom: 16px;
    right: 16px;
    width: 50px;
    height: 50px;

    :deep(.icon) svg {
      width: 25px !important;
      height: 25px !important;
    }
  }
}
</style>

<style>
body.drawer-open .floating-give-btn {
  display: none !important;
}
</style>
